import classNames from "classnames";
import { isEmpty, last } from "lodash";
import { CalendarMode, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import React, { forwardRef, JSX, useEffect, useMemo } from "react";

import { useBookingRowsContext } from "../context/booking_rows_context";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import css from "../side_panel_rows.module.scss";
import { useChildrenRows, useExpanded, usePagination, useRowMinHeight } from "./hooks";
import { RowContent } from "./row_content";
import { RowMenu } from "./row_menu";
import { RowPagination } from "./row_pagination";
import { SidePanelRowActions } from "./side_panel_row_actions";
import { useAllBookingsMode } from "./use_all_bookings_mode";

type SidePanelRowProps = {
  row: Row;
  scrollToDate: (date: string) => void;
};

export const SidePanelRow = forwardRef<HTMLDivElement, SidePanelRowProps>(
  ({ row, scrollToDate }, ref): JSX.Element => {
    const { calendarMode } = useBookingsOverviewContext();
    const { bookingsByRowKey, rowsLoadingByKey, rowsByCalendarMode } = useBookingRowsContext();

    const { currentPage, setCurrentPage, onTotalPagesChange, resetPagination } = usePagination();
    const { expanded, expandable, setExpanded, collapseRow } = useExpanded({
      row,
      onCollapse: resetPagination
    });
    const {
      pageableChildrenMeta,
      childrenCount,
      loading: childrenLoading
    } = useChildrenRows({
      row,
      expanded,
      currentPage,
      onSuccess: ({ meta: { totalPages } }, { unpaginated }) => !unpaginated && onTotalPagesChange(totalPages)
    });

    const { isAllBookingsModeEnabled, handleAllBookingsModeChange } = useAllBookingsMode({
      row,
      onAllBookingsModeEnable: () => !expanded && expandable && setExpanded(true),
      childrenCount
    });

    const rows = rowsByCalendarMode[calendarMode];
    const loading = childrenLoading || rowsLoadingByKey[row.key];
    const minHeight = useRowMinHeight({ row });
    const isFirstRow = rows?.[0]?.key === row.key;
    const isLastRow = last(rows)?.key === row.key;
    const shouldDisplayActivityActions = calendarMode === CalendarMode.Projects;

    const hasOnlyEmptyOtherChildrenRow = useMemo(() => {
      if (calendarMode !== CalendarMode.Workforce || row.type !== RowType.Profile) {
        return false;
      }
      const childrenRows = rows.filter(({ parents }) => parents.includes(row.key));
      const hasOnlyOtherRow = childrenRows.length === 1 && childrenRows[0].type === RowType.Other;
      if (!hasOnlyOtherRow) {
        return false;
      }
      const otherRow = childrenRows[0];
      return !rowsLoadingByKey[otherRow.key] && isEmpty(bookingsByRowKey[otherRow.key]);
    }, [calendarMode, row, rows, bookingsByRowKey, rowsLoadingByKey]);

    useEffect(() => {
      if (!expandable) {
        collapseRow();
        return;
      }
      const shouldCollapse = !expanded || ((!childrenCount || hasOnlyEmptyOtherChildrenRow) && !loading);
      if (shouldCollapse) {
        calendarMode === CalendarMode.Projects &&
          handleAllBookingsModeChange({ isAllBookingsModeEnabled: false, propagate: false });
        collapseRow();
      }
    }, [expandable, expanded, childrenCount, loading]);

    const toggleTreeItem = () => {
      if (expanded) {
        collapseRow();
        resetPagination();
      } else {
        setExpanded(true);
      }
    };

    return (
      <div
        ref={ref}
        className={classNames(css.item, {
          [css.child]: !isEmpty(row.parents),
          [css.loading]: loading,
          [css.opened]: expanded,
          [css.borderTop]: !isFirstRow,
          [css.borderBottom]: isLastRow
        })}
        style={{
          ...(!expandable && { minHeight })
        }}
      >
        {expanded && !!childrenCount && <div className={css.backgroundBox} />}
        <div className={css.rowContentAndMenuContainer}>
          <div className={css.rowContentContainer}>
            <RowContent
              row={row}
              expanded={expanded}
              expandable={expandable}
              loading={loading}
              onExpandedToggle={toggleTreeItem}
              scrollToDate={scrollToDate}
              childrenCount={childrenCount}
            />
          </div>
          {shouldDisplayActivityActions && (
            <SidePanelRowActions
              className={classNames(css.sidePanelRowActions, {
                [css.loading]: loading
              })}
              showAllBookingsAction={{
                isEnabled: isAllBookingsModeEnabled,
                onClick: () => handleAllBookingsModeChange({ isAllBookingsModeEnabled: false })
              }}
            />
          )}
          {row.type !== RowType.Other && (
            <RowMenu
              row={row}
              showAllBookingsAction={{
                isEnabled: isAllBookingsModeEnabled,
                onClick: () =>
                  handleAllBookingsModeChange({ isAllBookingsModeEnabled: !isAllBookingsModeEnabled })
              }}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          )}
        </div>
        <RowPagination
          row={row}
          meta={pageableChildrenMeta}
          loading={childrenLoading}
          expanded={expanded}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    );
  }
);

SidePanelRow.displayName = "SidePanelRow";
